import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Download, CheckCircle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailCaptureSection = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/email-leads`, { email: trimmed, source: 'free_guide' });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Could not save your email. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="free-guide"
      className="py-16 sm:py-20 relative overflow-hidden bg-slate-900"
      data-testid="email-capture-section"
    >
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl" aria-hidden="true"></div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-3xl mx-auto bg-slate-800/50 backdrop-blur-sm rounded-3xl p-8 sm:p-10 border border-slate-700/60 text-center">
          <div className="w-14 h-14 mx-auto mb-5 bg-emerald-500/15 rounded-2xl flex items-center justify-center border border-emerald-500/30">
            <Download className="w-7 h-7 text-emerald-400" aria-hidden="true" />
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
            Free Download: Health, Fitness &amp; Nutrition Guide
          </h2>
          <p className="text-slate-300 text-base sm:text-lg mb-6 max-w-xl mx-auto">
            Practical doctor-guided wellness tips, delivered to your inbox.
          </p>

          {submitted ? (
            <div
              className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-300"
              data-testid="email-success"
            >
              <CheckCircle className="w-5 h-5" aria-hidden="true" />
              Thank you! We&apos;ll send your guide shortly.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3 max-w-lg mx-auto" noValidate>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="sr-only" htmlFor="guide-email">Email address</label>
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" aria-hidden="true" />
                  <input
                    id="guide-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full bg-slate-900/70 border border-slate-700 focus:border-emerald-500 focus:outline-none rounded-full pl-10 pr-4 py-3 text-white placeholder:text-slate-500 text-sm sm:text-base"
                    data-testid="email-capture-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 disabled:opacity-60 text-white px-6 py-3 rounded-full font-semibold text-sm sm:text-base transition-all shadow-lg shadow-emerald-500/25 whitespace-nowrap"
                  data-testid="email-capture-submit-btn"
                >
                  {submitting ? 'Saving…' : 'Send Me the Free Guide'}
                </button>
              </div>
              {error && (
                <p className="text-rose-400 text-xs" data-testid="email-capture-error">{error}</p>
              )}
              <p className="flex items-center justify-center gap-1.5 text-slate-500 text-[11px] pt-1">
                <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
                Your email is securely saved in our admin dashboard.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default EmailCaptureSection;
